import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExportButton from '../components/ExportButton';
import { getJobStats, getJobs } from '../utils/api';
import { formatCurrency, formatDateShort, getStatusBadgeClass, getServiceFromJob } from '../utils/format';
import { useAuth } from '../context/AuthContext';

// ── date helpers (all local-time, date-only) ─────────────────────────────
const DAY = 86400000;
const toDate = (s) => (s ? new Date(String(s).slice(0, 10) + 'T00:00:00') : null);
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function startOfWeek(d) { // Monday
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  return x;
}
const WD = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// Pick a dot colour for a service name.
function serviceKind(name = '') {
  const s = name.toLowerCase();
  if (s.includes('roof')) return 'roof';
  if (s.includes('coat')) return 'coat';
  if (s.includes('paint')) return 'paint';
  return 'other';
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isAdmin, staff_name, username } = useAuth();

  useEffect(() => {
    Promise.all([getJobStats(), getJobs({ limit: 500 })])
      .then(([s, j]) => { setStats(s); setJobs(j.jobs || []); })
      .finally(() => setLoading(false));
  }, []);

  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(today), [today]);

  // This week's schedule: a job appears on every day between its start and end
  // dates; a job with only a start (or only a remaining-collection date) shows on
  // that single day.
  const week = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => ({ date: new Date(weekStart.getTime() + i * DAY), events: [] }));
    for (const job of jobs) {
      const start = toDate(job.start_date), end = toDate(job.end_date) || start;
      const svc = getServiceFromJob(job);
      const who = job.sales_rep || job.city || '';
      for (const d of days) {
        if (start && end && d.date >= start && d.date <= end) {
          const label = sameDay(d.date, start) ? (sameDay(start, end) ? svc : `${svc} · starts`) : sameDay(d.date, end) ? `${svc} · wraps up` : svc;
          d.events.push({ kind: serviceKind(svc), title: `${job.customer_name} · ${label}`, sub: who, po: job.po_number });
        }
        const rem = toDate(job.rem_date);
        if (rem && sameDay(rem, d.date)) d.events.push({ kind: 'money', title: `${job.customer_name} · collect balance`, sub: formatCurrency(job.rem_amt), po: job.po_number });
      }
    }
    return days;
  }, [jobs, weekStart]);

  // Pipeline: count by production status (top 6), blank = Not started.
  const pipeline = useMemo(() => {
    const counts = {};
    for (const j of jobs) {
      if ((j.sales_status || '').toLowerCase() === 'cancelled') continue;
      const k = j.prod_status || 'Not started';
      counts[k] = (counts[k] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [jobs]);
  const pipelineMax = Math.max(1, ...pipeline.map(p => p[1]));

  // Waiting on money: deposits and remaining balances marked Due.
  const owed = useMemo(() => {
    const list = [];
    for (const j of jobs) {
      if ((j.deposit_status || '') === 'Due' && j.deposit_amt) list.push({ job: j, type: 'Deposit', amt: j.deposit_amt });
      if ((j.rem_status || '') === 'Due' && j.rem_amt) list.push({ job: j, type: 'Balance', amt: j.rem_amt });
    }
    return list.sort((a, b) => b.amt - a.amt);
  }, [jobs]);
  const owedTotal = owed.reduce((s, o) => s + (o.amt || 0), 0);

  const recent = jobs.slice(0, 8);
  const m = stats?.moneySummary;
  const sold = m?.sold || 0, cost = m?.cost || 0, profit = m?.profit || 0;
  const margin = sold > 0 ? (profit / sold) * 100 : 0;
  const hasMoney = (m?.jobs_with_cost || 0) > 0 && sold > 0;

  const firstName = (staff_name || username || '').split(' ')[0];
  const greeting = today.getHours() < 12 ? 'Good morning' : today.getHours() < 17 ? 'Good afternoon' : 'Good evening';
  const dateLine = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <>
      <div className="page-header">
        <h2>Dashboard <span className="page-header-sub">{dateLine}</span></h2>
        <div className="page-header-actions">
          <ExportButton sheet="all" />
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => navigate('/add')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              Add record
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        <style>{`
          .dash-greet { font-family: var(--font-display); font-size: 30px; font-weight: 600; line-height: 1.1; margin-bottom: 4px; }
          .dash-greet em { font-style: italic; font-weight: 500; }
          .dash-sub { color: var(--gray-500); margin-bottom: var(--gap); }
          .wk { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; padding: 0 20px 20px; }
          .wk-day { min-height: 118px; padding: 10px 10px 8px; border-radius: 10px; background: var(--gray-50); }
          .wk-day.today { background: #fff; box-shadow: inset 0 0 0 1.5px var(--green-600); }
          .wk-day.wknd { background: transparent; box-shadow: inset 0 0 0 1px var(--gray-100); }
          .wk-day.today.wknd { box-shadow: inset 0 0 0 1.5px var(--green-600); }
          .wk-h { display: flex; align-items: baseline; gap: 6px; margin-bottom: 8px; }
          .wk-n { font-family: var(--font-display); font-size: 24px; font-weight: 600; line-height: 1; }
          .wk-w { font-size: 11px; letter-spacing: .06em; color: var(--gray-500); font-weight: 500; }
          .wk-day.today .wk-n, .wk-day.today .wk-w { color: var(--green-800); }
          .ev { display: flex; gap: 7px; align-items: flex-start; background: #fff; border-radius: 7px; padding: 6px 8px; margin-top: 5px; font-size: 12px; line-height: 1.3; box-shadow: 0 1px 2px rgba(15,31,26,.06); cursor: pointer; }
          .wk-day.today .ev { background: var(--green-100); }
          .ev i { flex: none; width: 7px; height: 7px; border-radius: 50%; margin-top: 4px; background: var(--green-600); }
          .ev i.roof { background: var(--teal-600); } .ev i.coat { background: var(--green-600); } .ev i.paint { background: var(--gold-500); } .ev i.other { background: var(--gray-400); } .ev i.money { background: var(--gold-500); }
          .ev b { display: block; font-weight: 500; color: var(--gray-900); overflow: hidden; text-overflow: ellipsis; }
          .ev span { color: var(--gray-500); font-size: 11px; }
          .wk-none { font-size: 11.5px; color: var(--gray-400); padding: 6px 2px; }
          .legend { display: flex; gap: 14px; font-size: 11.5px; color: var(--gray-500); }
          .legend span { display: inline-flex; align-items: center; gap: 6px; }
          .legend i { width: 7px; height: 7px; border-radius: 50%; }
          .pipe > div { display: grid; grid-template-columns: 110px 1fr 32px; gap: 12px; align-items: center; height: 30px; font-size: 13px; }
          .pipe .bar { height: 6px; background: var(--gray-50); border-radius: 3px; overflow: hidden; }
          .pipe .bar i { display: block; height: 100%; background: var(--green-600); border-radius: 3px; }
          .pipe > .empty { display: block; height: auto; }
          .pipe b { text-align: right; font-weight: 500; font-variant-numeric: tabular-nums; }
          .kv > div { display: grid; grid-template-columns: 1fr auto; align-items: center; height: 40px; border-top: 1px solid var(--gray-100); font-size: 13px; }
          .kv > .empty { display: block; height: auto; padding: 6px 0 4px; border-top: 0; color: var(--gray-500); font-size: 13px; line-height: 1.5; }
          .kv > div:first-child { border-top: 0; }
          .kv b { font-family: var(--font-display); font-size: 20px; font-weight: 600; font-variant-numeric: tabular-nums; }
          .kv .profit b { color: var(--green-800); }
          .owed > div { grid-template-columns: 1fr 72px 90px; gap: 12px; cursor: pointer; }
          .owed .tag { font-size: 11px; padding: 2px 0; border-radius: 6px; text-align: center; font-weight: 500; background: var(--green-100); color: var(--green-800); }
          .owed .tag.bal { background: var(--gold-100); color: var(--gold-600); }
          .owed .amt { text-align: right; font-weight: 500; font-variant-numeric: tabular-nums; }
          .owed .who { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        `}</style>

        <div className="dash-greet">{greeting}{firstName ? <>, <em>{firstName}</em></> : null}</div>
        <div className="dash-sub">
          {stats?.active ?? 0} active jobs · {week[(today.getDay() + 6) % 7].events.length} on the schedule today · {owed.length} payment{owed.length === 1 ? '' : 's'} waiting
        </div>

        <div className="grid12">
          {/* Stat tiles */}
          <div className="stat-card hero c3">
            <div className="stat-label">Active contract value</div>
            <div><div className="stat-value" style={{ fontSize: 34 }}>{formatCurrency(stats?.active_contract_value || 0)}</div><div className="stat-sub">{stats?.active ?? 0} active jobs</div></div>
          </div>
          <div className="stat-card c3">
            <div className="stat-label">Jobs on the books</div>
            <div><div className="stat-value">{stats?.total ?? 0}</div><div className="stat-sub">{formatCurrency(stats?.total_contract_value || 0)} total contract value</div></div>
          </div>
          <div className="stat-card c3">
            <div className="stat-label">Closed &amp; funded</div>
            <div><div className="stat-value">{stats?.closed ?? 0}</div><div className="stat-sub">{stats?.cancelled ?? 0} cancelled</div></div>
          </div>
          <div className="stat-card gold c3">
            <div className="stat-label">Waiting on money</div>
            <div><div className="stat-value" style={{ fontSize: 34 }}>{formatCurrency(owedTotal)}</div><div className="stat-sub">{owed.filter(o => o.type === 'Deposit').length} deposits · {owed.filter(o => o.type === 'Balance').length} balances</div></div>
          </div>

          {/* This week */}
          <div className="card c12">
            <div className="card-header">
              <h3>This week</h3>
              <div className="legend">
                <span><i style={{ background: 'var(--teal-600)' }} />Roofing</span>
                <span><i style={{ background: 'var(--green-600)' }} />Coating</span>
                <span><i style={{ background: 'var(--gold-500)' }} />Painting / money</span>
                <span><i style={{ background: 'var(--gray-400)' }} />Other</span>
              </div>
            </div>
            <div className="wk">
              {week.map((d, i) => (
                <div key={i} className={`wk-day${sameDay(d.date, today) ? ' today' : ''}${i >= 5 ? ' wknd' : ''}`}>
                  <div className="wk-h"><span className="wk-n">{d.date.getDate()}</span><span className="wk-w">{WD[i]}</span></div>
                  {d.events.length === 0
                    ? <div className="wk-none">{jobs.length === 0 && i === 0 ? 'Jobs with start and end dates appear here' : 'Nothing scheduled'}</div>
                    : d.events.slice(0, 4).map((e, k) => (
                      <div key={k} className="ev" onClick={isAdmin ? () => navigate(`/update?po=${e.po}`) : undefined} title={e.title}>
                        <i className={e.kind} /><div><b>{e.title}</b>{e.sub ? <span>{e.sub}</span> : null}</div>
                      </div>
                    ))}
                  {d.events.length > 4 && <div className="wk-none">+{d.events.length - 4} more</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Pipeline */}
          <div className="card c4">
            <div className="card-header"><h3>Pipeline</h3><small>Jobs by production status</small></div>
            <div className="card-body pipe">
              {pipeline.length === 0
                ? <div className="empty" style={{ color: 'var(--gray-500)', padding: '6px 0', fontSize: 13 }}>No jobs yet — add a record and it will appear here by production status.</div>
                : pipeline.map(([name, n]) => (
                  <div key={name}><span>{name}</span><div className="bar"><i style={{ width: `${(n / pipelineMax) * 100}%` }} /></div><b>{n}</b></div>
                ))}
            </div>
          </div>

          {/* Sold / cost / profit */}
          <div className="card c4">
            <div className="card-header"><h3>Sold, cost &amp; profit</h3><small>Jobs with a saved cost</small></div>
            <div className="card-body kv">
              {!hasMoney ? (
                <div className="empty">No jobs have a saved calculator cost yet. Open a record, use the Calculator tab and click "Save these numbers to the job".</div>
              ) : (
                <>
                  <div><span>Sold</span><b>{formatCurrency(sold)}</b></div>
                  <div><span>Cost</span><b>{formatCurrency(cost)}</b></div>
                  <div className="profit"><span>Profit</span><b>{formatCurrency(profit)}</b></div>
                  <div><span>Margin</span><b>{margin.toFixed(1)}%</b></div>
                </>
              )}
            </div>
          </div>

          {/* Waiting on money */}
          <div className="card c4">
            <div className="card-header"><h3>Waiting on money</h3><small>{owed.length} item{owed.length === 1 ? '' : 's'}</small></div>
            <div className="card-body kv owed">
              {owed.length === 0
                ? <div className="empty">Nothing outstanding — every deposit and balance is collected.</div>
                : owed.slice(0, 6).map((o, i) => (
                  <div key={i} onClick={isAdmin ? () => navigate(`/update?po=${o.job.po_number}`) : undefined}>
                    <span className="who">{o.job.customer_name}{o.job.city ? ` · ${o.job.city}` : ''}</span>
                    <span className={`tag${o.type === 'Balance' ? ' bal' : ''}`}>{o.type}</span>
                    <span className="amt">{formatCurrency(o.amt)}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Recent jobs */}
          <div className="card c12">
            <div className="card-header">
              <h3>Recent jobs</h3>
              <button className="btn btn-outline btn-sm" onClick={() => navigate('/tracker')}>View all</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>PO</th><th>Customer</th><th>City</th><th>Service</th><th>Sales rep</th><th style={{ textAlign: 'right' }}>Contract</th><th>Sales</th><th>Production</th><th>Start</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 && (
                    <tr><td colSpan={9} className="wk-none" style={{ textAlign: 'center', height: 64 }}>No jobs yet. Add your first record to see it here.</td></tr>
                  )}
                  {recent.map(job => (
                    <tr key={job.id} onClick={isAdmin ? () => navigate(`/update?po=${job.po_number}`) : undefined} style={{ cursor: isAdmin ? 'pointer' : 'default' }}>
                      <td className="td-po">{job.po_number}</td>
                      <td className="td-name">{job.customer_name}</td>
                      <td>{job.city || '—'}</td>
                      <td>{getServiceFromJob(job)}</td>
                      <td>{job.sales_rep || '—'}</td>
                      <td className="td-amt" style={{ textAlign: 'right' }}>{formatCurrency(job.change_order_amt || job.contract_amt)}</td>
                      <td><span className={getStatusBadgeClass(job.sales_status)}>{job.sales_status || '—'}</span></td>
                      <td>{job.prod_status || '—'}</td>
                      <td>{formatDateShort(job.start_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
